import React, { useState } from "react";
import {
  VStack,
  HStack,
  Text,
  Tag,
  Card,
  Badge,
  Avatar,
  Box,
  Flex,
  Switch,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FaUserSecret } from "react-icons/fa";
import { formatTimeStamp } from "../../../logics/timeLogic";
import axiosInstance from "../../../routes/axiosInstance";

const ParticipantsTab = ({
  members = [],
  spaceId,
  onMemberStatusUpdate,
  currentUser,
}) => {
  if (members.length === 0) {
    return (
      <Card p={8} borderRadius="xl" boxShadow="md" textAlign="center">
        <Text color="gray.500" fontSize="lg">
          No participants found
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          Share your space link to invite participants
        </Text>
      </Card>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Card p={4} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Participants
          </Text>
          <Badge
            colorScheme="teal"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {members.length} participant{members.length !== 1 ? "s" : ""}
          </Badge>
        </HStack>

        <VStack align="stretch" spacing={3}>
          {members.map((member) => (
            <ParticipantCard
              key={member._id || member.id}
              member={member}
              spaceId={spaceId}
              onMemberStatusUpdate={onMemberStatusUpdate}
              currentUser={currentUser}
            />
          ))}
        </VStack>
      </Card>
    </VStack>
  );
};

const ParticipantCard = ({
  member,
  spaceId,
  onMemberStatusUpdate,
  currentUser,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  const handleStatusToggle = async (newStatus) => {
    setIsUpdating(true);
    try {
      const response = await axiosInstance.patch(
        `/api/spaces/${spaceId}/members/${member._id || member.id}/status`,
        { isActive: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentUser?.token}`,
          },
        },
      );

      if (response.status === 200) {
        toast({
          title: `Member ${newStatus ? "activated" : "deactivated"}`,
          description: `${member.alias} has been ${
            newStatus ? "activated" : "deactivated"
          } successfully`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        // Update the member status in parent component if callback provided
        if (onMemberStatusUpdate) {
          onMemberStatusUpdate(member._id || member.id, newStatus);
        }
      }
    } catch (error) {
      console.error("Error updating member status:", error);
      toast({
        title: "Error updating status",
        description:
          error.response?.data?.error || "Failed to update member status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card
      p={4}
      borderRadius="lg"
      border="1px"
      borderColor={member.isActive !== false ? "gray.200" : "red.200"}
      bg={member.isActive !== false ? "white" : "red.50"}
      opacity={member.isActive !== false ? 1 : 0.7}
      _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
      transition="all 0.2s"
    >
      <Flex align="center" justify="space-between">
        <HStack spacing={3}>
          <Avatar size="md" name={member.alias} />

          <Box>
            <HStack spacing={2} align="center">
              <Text fontWeight="bold" fontSize="lg">
                {member.alias}
              </Text>

              {member.isAnon && (
                <Tag size="sm">
                  <FaUserSecret title="Anonymous user" />
                </Tag>
              )}

              {member.isActive === false && (
                <Tag size="sm" colorScheme="red" variant="solid">
                  Inactive
                </Tag>
              )}
            </HStack>

            <Text fontSize="sm" color="gray.600">
              Joined: {formatTimeStamp(member.joinedAt)}
            </Text>
          </Box>
        </HStack>

        <VStack align="end" spacing={1}>
          {/* Status Toggle */}
          <VStack align="end" spacing={2}>
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600">
                {member.isActive !== false ? "Active" : "Inactive"}
              </Text>
              <Switch
                size="sm"
                isChecked={member.isActive !== false}
                onChange={(e) => handleStatusToggle(e.target.checked)}
                isDisabled={isUpdating}
                colorScheme="teal"
              />
              {isUpdating && <Spinner size="xs" />}
            </HStack>

            {/* Activity indicator */}
            {member.activity && (
              <ActivityIndicator activity={member.activity} />
            )}
          </VStack>
        </VStack>
      </Flex>
    </Card>
  );
};

const ActivityIndicator = ({ activity }) => {
  switch (activity.activityType) {
    case "reaction":
      return activity.emoji ? (
        <Tag
          size="sm"
          colorScheme="yellow"
          variant="subtle"
          borderRadius="full"
        >
          <HStack spacing={1}>
            <Text fontSize="xs">Reacted:</Text>
            <Text fontSize="lg">{activity.emoji}</Text>
          </HStack>
        </Tag>
      ) : null;

    case "pollVote":
      return (
        <Tag size="sm" colorScheme="blue" variant="subtle" borderRadius="full">
          <Text fontSize="xs">Voted</Text>
        </Tag>
      );

    case "formResponse":
      return (
        <Tag size="sm" colorScheme="green" variant="subtle" borderRadius="full">
          <Text fontSize="xs">Responded</Text>
        </Tag>
      );

    default:
      return (
        <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="full">
          <Text fontSize="xs">Active</Text>
        </Tag>
      );
  }
};

export default ParticipantsTab;
